import React, { useState } from "react";
import { Button, Input } from "reactstrap";
import moment from "moment";

// Passo de coleta da cotação (PO1-2792 e-mail; PO1-2576 API da transportadora).
// Componente puro: recebe o subdoc `coleta`, as `opcoes` do backend
// (GET .../coleta/opcoes: canal api|email, conector, dataPadrao, cancelavel,
// porNota, modoTeste) e os handlers. Quem decide API x e-mail é o backend; aqui
// só se mostra o estado e se coleta a data (decisão 3A: próximo dia útil,
// editável) e o canal (botão "Enviar por e-mail" ao lado da falha da API).

const formatInstant = (d) => (d ? moment(d).format("DD/MM/YYYY HH:mm") : "—");
const formatDia = (d) => (d ? moment(d).format("DD/MM/YYYY") : "—");
const isPersonLike = (v) => v && typeof v === "object" && ("email" in v || "nome" in v);
const personStr = (v) => (isPersonLike(v) ? v.nome || v.email || "—" : v == null ? "—" : String(v));
const COLETA_MODO_LABELS = { automatico: "automática na aprovação", manual: "pedida manualmente" };
const coletaModoStr = (modo) => COLETA_MODO_LABELS[modo] || (modo ? String(modo) : "");
const CONECTOR_LABELS = {
  "correios-cws": "Correios",
  rodonaves: "Rodonaves",
  ssw: "SSW",
  "melhor-envio": "Melhor Envio",
  mandae: "Mandaê",
  loggi: "Loggi",
  lalamove: "Lalamove",
  "uber-direct": "Uber Direct",
  "jt-express": "J&T Express",
  track3r: "Buslog (TRACK3R)",
  "total-express": "Total Express",
};
const conectorLabel = (k) => CONECTOR_LABELS[k] || k || "a transportadora";
// Texto para o usuário: "pela integração com <transportadora>". SSW é a
// plataforma, não a transportadora: nesse caso usa o nome da cotação.
const integracaoCom = (k, transportadoraNome) => {
  const nome = k && k !== "ssw" && CONECTOR_LABELS[k] ? CONECTOR_LABELS[k] : transportadoraNome || "a transportadora";
  return `pela integração com ${nome}`;
};

const btnStyle = { fontSize: 12, padding: "5px 14px" };

export const STATUS_API = {
  solicitada: { rotulo: "Coleta solicitada", tom: "success" },
  "dry-run": { rotulo: "Coleta simulada (modo teste)", tom: "info" },
  "aguardando-etiqueta": { rotulo: "Coleta aguardando a etiqueta", tom: "warning" },
  falha: { rotulo: "Falha ao pedir a coleta", tom: "danger" },
  cancelada: { rotulo: "Coleta cancelada", tom: "danger" },
};

function tomDe(coleta, cancelada) {
  if (cancelada) return "danger";
  if (!coleta) return "neutral";
  if (coleta.canal === "api") return (STATUS_API[coleta.status] || STATUS_API.solicitada).tom;
  return "success";
}

export default function ColetaPasso({
  coleta,
  opcoes,
  carregandoOpcoes,
  canSolicitar,
  canCancelar,
  busy,
  enviando,
  onSolicitar, // ({ dataColeta, canal }) => Promise
  onCancelar, // (motivo) => Promise<boolean>
  inputStyle,
  transportadoraNome,
}) {
  const [confirm, setConfirm] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [dataColeta, setDataColeta] = useState("");

  const cancelada = !!(coleta && coleta.canceladaEm);
  const viaApi = !!(coleta && coleta.canal === "api" && !cancelada);
  const canalApi = !!(opcoes && opcoes.canal === "api");
  const porNota = !!(opcoes && opcoes.porNota);
  const dataEfetiva = dataColeta || (opcoes && opcoes.dataPadrao) || "";
  const destinatarios = coleta && Array.isArray(coleta.destinatarios) ? coleta.destinatarios.filter(Boolean) : [];
  const motivoOk = motivo.trim().length >= 3;
  const tom = tomDe(coleta, cancelada);
  const estilo = inputStyle || (() => ({}));

  const solicitar = async (canal) => {
    if (!canSolicitar || busy) return;
    try {
      await onSolicitar({ dataColeta: canalApi && canal !== "email" ? dataEfetiva || null : null, canal: canal || null });
    } finally {
      setConfirm(false);
    }
  };

  const cancelar = async () => {
    if (!canCancelar || busy || !motivoOk) return;
    const ok = await onCancelar(motivo.trim());
    if (ok) {
      setCancelOpen(false);
      setMotivo("");
    }
  };

  const ativa = !!(coleta && coleta.solicitadaEm && !cancelada);

  // ── texto do estado ──
  let estado;
  if (cancelada) {
    estado = (
      <span title={destinatarios.length ? `Cancelamento enviado para: ${destinatarios.join(", ")}` : undefined}>
        <strong>Coleta cancelada</strong> em {formatInstant(coleta.canceladaEm)} por {personStr(coleta.canceladaPor)}
        {" "}(solicitada em {formatInstant(coleta.solicitadaEm)}
        {coleta.numero ? `, nº ${coleta.numero}` : ""})
        {coleta.motivoCancelamento ? <><br />Motivo: {coleta.motivoCancelamento}</> : null}
      </span>
    );
  } else if (viaApi) {
    const meta = STATUS_API[coleta.status] || STATUS_API.solicitada;
    estado = (
      <span data-testid="coleta-estado-api">
        <strong>{meta.rotulo}</strong>
        {coleta.status === "aguardando-etiqueta" ? (
          <> — a coleta dos {conectorLabel(coleta.conectorKey)} é pedida depois de gerar a etiqueta na NF (Consulta de NF-e, aba Etiqueta).</>
        ) : (
          <>
            {" "}{integracaoCom(coleta.conectorKey, transportadoraNome)} em {formatInstant(coleta.solicitadaEm)} por {personStr(coleta.solicitadaPor)}
            {coleta.numero ? <> · nº <strong>{coleta.numero}</strong></> : null}
            {coleta.protocolo ? <> · protocolo {coleta.protocolo}</> : null}
            {coleta.dataAgendada ? <> · agendada para {formatDia(coleta.dataAgendada)}</> : null}
          </>
        )}
        {coleta.status === "dry-run" ? (
          <span className="lc-badge lc-badge-coleta-teste" style={{ marginLeft: 8 }} title="Vínculo em modo teste: nada foi enviado à transportadora">Modo teste</span>
        ) : null}
        {coleta.status === "falha" && coleta.erro ? <><br /><span style={{ color: "var(--everlog-status-danger)" }}>{coleta.erro}</span></> : null}
      </span>
    );
  } else if (coleta && coleta.solicitadaEm) {
    estado = (
      <span title={destinatarios.length ? `Destinatários: ${destinatarios.join(", ")}` : undefined}>
        <strong>Coleta solicitada</strong> por e-mail em {formatInstant(coleta.solicitadaEm)} por {personStr(coleta.solicitadaPor)}
        {" "}({coletaModoStr(coleta.modo) || "—"}{Number(coleta.envios) > 1 ? `, ${Number(coleta.envios)} envios` : ""})
      </span>
    );
  } else if (carregandoOpcoes) {
    estado = <span>Verificando a integração da transportadora...</span>;
  } else if (canalApi && porNota) {
    estado = <span>A coleta dos {conectorLabel(opcoes.conector)} é pedida depois de gerar a etiqueta na NF (Consulta de NF-e, aba Etiqueta).</span>;
  } else if (canalApi) {
    estado = (
      <span>
        Coleta ainda não solicitada. Será pedida {integracaoCom(opcoes.conector, transportadoraNome)}
        {opcoes.modoTeste ? <span className="lc-badge lc-badge-coleta-teste" style={{ marginLeft: 8 }}>Modo teste</span> : null}
        {opcoes.aviso ? <><br /><small>{opcoes.aviso}</small></> : null}
      </span>
    );
  } else {
    estado = <span>Coleta ainda não solicitada à transportadora{opcoes && opcoes.aviso ? ` (${opcoes.aviso})` : ""}.</span>;
  }

  // ── botão principal ──
  const falhou = viaApi && coleta.status === "falha";
  const rotuloPrincipal = cancelada
    ? "Solicitar coleta novamente"
    : falhou
      ? "Tentar de novo"
      : ativa
        ? "Reenviar solicitação"
        : "Solicitar coleta" + (canalApi ? "" : " à transportadora");
  const precisaConfirmar = ativa && !falhou;
  const falhouOuCancelada = falhou || cancelada;

  return (
    <div
      data-testid="coleta-passo"
      style={{
        background: `color-mix(in srgb, var(--everlog-status-${tom}) ${tom === "neutral" ? 12 : 10}%, transparent)`,
        borderLeft: `3px solid var(--everlog-status-${tom})`,
        borderRadius: 4, padding: "8px 12px", marginBottom: 20,
        fontSize: 12, color: "var(--everlog-card-text)", lineHeight: 1.5,
        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}
    >
      {estado}

      {/* Correios (coleta por NF): nada a pedir aqui, nem antes nem depois de
          registrar "aguardando etiqueta" — a coleta sai pela aba Etiqueta da NF. */}
      {canSolicitar && !(canalApi && porNota && !falhouOuCancelada) && (
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {canalApi && !confirm && (
            <label style={{ display: "flex", alignItems: "center", gap: 6, margin: 0, fontSize: 11 }}>
              Data da coleta
              <Input
                type="date"
                bsSize="sm"
                value={dataEfetiva}
                min={moment().format("YYYY-MM-DD")}
                onChange={(e) => setDataColeta(e.target.value)}
                disabled={busy}
                style={{ ...estilo(false), width: 150, fontSize: 12, padding: "3px 8px" }}
                data-testid="coleta-data"
              />
            </label>
          )}
          {!precisaConfirmar || !confirm ? (
            <Button
              color="info" size="sm"
              onClick={() => { if (precisaConfirmar) setConfirm(true); else solicitar(null); }}
              disabled={busy || (canalApi && !dataEfetiva)}
              style={btnStyle}
              data-testid="coleta-solicitar"
            >
              {enviando ? "Enviando..." : (
                <>
                  <i className="tim-icons icon-send" style={{ marginRight: 6, fontSize: 11 }} />
                  {rotuloPrincipal}
                </>
              )}
            </Button>
          ) : (
            <>
              <Button color="info" size="sm" onClick={() => solicitar(null)} disabled={busy} style={btnStyle}>
                {enviando ? "Enviando..." : "Confirmar reenvio"}
              </Button>
              <Button className="lc-btn-cancel" size="sm" onClick={() => setConfirm(false)} disabled={busy} style={btnStyle}>
                Voltar
              </Button>
            </>
          )}
          {falhou && (
            <Button
              className="lc-btn-cancel" size="sm"
              onClick={() => solicitar("email")}
              disabled={busy}
              style={btnStyle}
              title="Envia a solicitação por e-mail à transportadora (fluxo manual)"
              data-testid="coleta-email"
            >
              Enviar por e-mail
            </Button>
          )}
          {canCancelar && !cancelOpen && !confirm && (
            <Button color="danger" size="sm" onClick={() => setCancelOpen(true)} disabled={busy} style={btnStyle}>
              <i className="tim-icons icon-simple-remove" style={{ marginRight: 6, fontSize: 11 }} />
              Cancelar coleta
            </Button>
          )}
        </div>
      )}

      {!canSolicitar && canCancelar && !cancelOpen && (
        <div style={{ marginLeft: "auto" }}>
          <Button color="danger" size="sm" onClick={() => setCancelOpen(true)} disabled={busy} style={btnStyle}>
            <i className="tim-icons icon-simple-remove" style={{ marginRight: 6, fontSize: 11 }} />
            Cancelar coleta
          </Button>
        </div>
      )}

      {canCancelar && cancelOpen && (
        <div style={{ flexBasis: "100%", display: "flex", flexDirection: "column", gap: 6, marginTop: 4 }}>
          <div style={{ fontSize: 11, color: "var(--everlog-card-muted)" }}>
            {viaApi
              ? (opcoes && opcoes.cancelavel === false
                  ? `A integração não cancela coletas nesta transportadora: o Hub registra o cancelamento, mas avise a transportadora diretamente.`
                  : `Motivo do cancelamento (obrigatório). O cancelamento é enviado ${integracaoCom(coleta.conectorKey, transportadoraNome)}.`)
              : `Motivo do cancelamento (obrigatório; vai no e-mail para ${destinatarios.join(", ") || "a transportadora"})`}
          </div>
          <Input
            type="textarea" rows={2} value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ex.: pedido adiado pelo cliente"
            maxLength={1000}
            style={{ ...estilo(false), resize: "vertical" }}
          />
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <Button color="danger" size="sm" onClick={cancelar} disabled={busy || !motivoOk} style={btnStyle}>
              {enviando ? "Enviando..." : "Confirmar cancelamento"}
            </Button>
            <Button className="lc-btn-cancel" size="sm" onClick={() => { setCancelOpen(false); setMotivo(""); }} disabled={busy} style={btnStyle}>
              Voltar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
